import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ContactPage from './pages/ContactPage';
import { AuthProvider } from './contexts/AuthContext';
import ToastProvider from './components/ui/ToastProvider';
import ScrollToTop from './components/ScrollToTop';
import RouteLoading from './components/ui/RouteLoading';
import PublicLayout from './components/Layout/PublicLayout';
import publicRoutes from './routes/publicRoutes';
import ClientSignIn from './pages/refactored/ClientSignIn';
import ClientPortal from './pages/refactored/ClientPortal';
import PublicLayoutV2 from './components/publicV2/PublicLayoutV2';
import HomeV2 from './pages/publicV2/HomeV2';
import { ServicesV2, ServiceDetailV2 } from './pages/publicV2/ServicesV2';
import PlatformV2 from './pages/publicV2/PlatformV2';
import SegmentPageV2 from './pages/publicV2/SegmentPageV2';
import HowItWorksV2 from './pages/publicV2/HowItWorksV2';
import AcceleratorV2 from './pages/publicV2/AcceleratorV2';
import DemoV2 from './pages/publicV2/DemoV2';
import { readySegments } from './config/v2Segments';
import OpportunityLabV2 from './pages/publicV2/OpportunityLabV2';
import TryV2 from './pages/publicV2/TryV2';
import PrivacyV2 from './pages/publicV2/PrivacyV2';
import PricingV2 from './pages/publicV2/PricingV2';
import StoriesV2 from './pages/publicV2/StoriesV2';
import StoryDetailV2 from './pages/publicV2/StoryDetailV2';
import CapstoneRecordPage from './pages/CapstoneRecordPage';
import PublicPortfolioProfilePage from './pages/PublicPortfolioProfilePage';
import adminRoutes from './routes/adminRoutes';
import portalRoutes from './routes/portalRoutes';
import referralRoutes from './routes/referralRoutes';
import AlumniChampionPage from './pages/AlumniChampionPage';
import UtilityCoopLandingPage from './pages/UtilityCoopLandingPage';
import UtilityIOULandingPage from './pages/UtilityIOULandingPage';
import FreightBrokerageLandingPage from './pages/FreightBrokerageLandingPage';
import AIXceleratorLandingPage from './pages/AIXceleratorLandingPage';
import AIPilotLandingPage from './pages/AIPilotLandingPage';
import AIPilotVerticalPage from './pages/AIPilotVerticalPage';
import PublicPortfolioPage from './pages/PublicPortfolioPage';
import ManagementPreviewPage from './pages/ManagementPreviewPage';

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
      <ScrollToTop />
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route path="/alumni-ai-champion" element={<AlumniChampionPage />} />
          <Route path="/utility-ai" element={<UtilityCoopLandingPage />} />
          <Route path="/utility-iou" element={<UtilityIOULandingPage />} />
          <Route path="/iou-demo" element={<UtilityIOULandingPage forcePresenter defaultRole="ceo" />} />
          <Route path="/freight-ai" element={<FreightBrokerageLandingPage />} />
          <Route path="/aixcelerator" element={<AIXceleratorLandingPage />} />
          <Route path="/ai-pilot" element={<AIPilotLandingPage />} />
          <Route path="/ai-pilot/transport" element={<AIPilotVerticalPage variantKey="transport" />} />
          <Route path="/ai-pilot/construction" element={<AIPilotVerticalPage variantKey="construction" />} />
          <Route path="/ai-pilot/care" element={<AIPilotVerticalPage variantKey="care" />} />
          <Route path="/portfolio/share/:token" element={<PublicPortfolioPage />} />
          <Route path="/try" element={<ManagementPreviewPage />} />
          {adminRoutes}
          {portalRoutes}
          {referralRoutes}
          {/*
              CUTOVER, 2026-08-13: V2 IS the public site. It was mounted at /v2 as a
              preview; it now owns "/".

              The old marketing pages are retired in routes/publicRoutes.tsx, where
              their paths redirect to the nearest V2 equivalent rather than 404 --
              inbound links and search results still point at them. The functional
              public routes (enrolment and payment, sponsor dashboard, challenge,
              leaderboard, pilot and membership pages) are untouched.

              This block must stay ABOVE the PublicLayout block below, so that "/"
              resolves to V2 rather than to the old home page.
          */}
          <Route path="/" element={<PublicLayoutV2 />}>
            <Route index element={<HomeV2 />} />
            <Route path="services" element={<ServicesV2 />} />
            <Route path="services/:slug" element={<ServiceDetailV2 />} />
            <Route path="platform" element={<PlatformV2 />} />
            {/*
                /proof IS the published records now. Ali, 2026-09-04: "This
                stories page needs to be integrated into the proof page or take
                it over so Case Studies can be reviewed by people visiting our
                website." It took it over.

                The old ProofV2 explained a standard and then promised, in future
                tense, that each record WOULD carry its evidence class and the
                artifact behind it - a capability that had already shipped. So the
                page a visitor reached from the header was describing as roadmap
                the very thing the site could already show them. The evidence
                standard survives as a band on the index (`StoriesStandardBand`);
                the promise is replaced by the records that keep it.
            */}
            {/*
                The buyer-segment pages from the C5.1 information architecture. One route
                per segment, all mounting the same template, because C5.1 settled that the
                four share a template so they cannot drift into four different arguments.

                Only the ready segments are declared. Ali chose higher education first on
                18 October 2026, since C2.5 approved it as the beachhead. `SegmentPageV2`
                renders nothing for a slug that is not ready, so an unwritten page cannot
                reach a visitor even if a route were added ahead of its content.

                These routes were verified free against the real route tree in C5.1, which
                is not a formality: `/partners` was the obvious choice for the ecosystem
                page and is already a live page.
            */}
            {/*
                C5.3 and C5.4. Both routes were verified free against the real route tree in
                C5.1, and both mount here rather than under the legacy block so they get the
                V2 nav and footer.
            */}
            <Route path="how-it-works" element={<HowItWorksV2 />} />
            <Route path="accelerator" element={<AcceleratorV2 />} />
            {/* C5.9. Reads nothing from the backend, by design: a page that cannot reach
                a tenant cannot leak one. */}
            <Route path="demo" element={<DemoV2 />} />
            {readySegments().map((s) => (
              <Route key={s.slug} path={s.slug} element={<SegmentPageV2 slug={s.slug} />} />
            ))}
            <Route path="proof" element={<StoriesV2 />} />
            <Route path="lab" element={<OpportunityLabV2 />} />
            {/* NOT "try": /try is the real product workspace
                (ManagementPreviewPage, declared above). Mounting the V2 explainer
                there post-cutover would shadow the product itself. */}
            <Route path="free-workspace" element={<TryV2 />} />
            {/*
                /contact moved OUT of the legacy publicRoutes block and under
                PublicLayoutV2. Ali, 2026-08-21: "Talk to an Architect takes me
                to a page that needs the navigation updated. That's the case
                throughout the application."

                It was rendering the old Home / The Program / Contact nav purely
                because it sat outside this layout. ContactPage renders no header
                of its own, so it simply inherits PublicHeaderV2 here -- no
                double header.

                THE SAME FIX APPLIES to the other legacy marketing pages still in
                publicRoutes.tsx. They were left there deliberately (that block
                carries /enroll and the payment flow), but the ones that are
                purely marketing should move here too.
            */}
            <Route path="contact" element={<ContactPage />} />
            <Route path="privacy" element={<PrivacyV2 />} />
            {/*
                /start renders SignupV2 no longer. Ali, 2026-08-21, asked three
                times: "Startfree page should go to try page." Every "Start free"
                button now points at /try directly; this redirect catches the
                inbound links, the sitemap and anyone who typed it.

                SignupV2 is left in the tree ON PURPOSE -- it is the
                account-creation page, and /try's own "Make this real: create
                your free account" button is where that flow belongs. Check what
                that button does before calling SignupV2 dead code.
            */}
            <Route path="start" element={<Navigate to="/try" replace />} />
            <Route path="pricing" element={<PricingV2 />} />
            {/*
                The index moved to /proof, so this is a redirect rather than a
                second copy of the same list on a second URL - two addresses for
                one page splits inbound links and gives search engines a
                duplicate to choose between.

                THE DETAIL PAGES DO NOT MOVE. `caseStudyPublicProjection` derives
                `seo.canonicalUrl` as `/stories/<slug>`, and that address is baked
                into every published snapshot and every share link already sent.
                Moving them would rewrite the address of live pages for a tidier
                tree - see the warning in `caseStudyAdminPublicationGuard`.
            */}
            <Route path="stories" element={<Navigate to="/proof" replace />} />
            {/*
                The published-record detail surface. Declared WITHOUT a leading
                slash, like every other child of this layout route -- a leading
                slash here would make it an absolute path and it would not nest.

                DELIBERATELY NOT LAZY. The shared cbv2- primitives this page
                leans on (.cbv2-pagehero, .cbv2-section, .cbv2-btn, .cbv2-rv)
                live in homeV2.css / servicesV2.css / publicV2.css /
                cinematicV2.css and are only in the bundle because these V2 pages
                are imported statically here. Splitting this route out would ship
                it without its own layout primitives -- see the warning at the
                top of proofV2.css.
            */}
            <Route path="stories/:slug" element={<StoryDetailV2 />} />
            {/*
                A student's shareable Capstone Record. Lives under PublicLayoutV2
                rather than in the legacy publicRoutes block, and the layout is the
                whole point rather than a detail:

                  - V2 is the site's real header. A page a student sends to a hiring
                    manager showing the retired navbar reads as a dead corner of the
                    site, which is the opposite of what the page is for.
                  - PublicLayout (V1) calls initTracker() UNCONDITIONALLY. That would
                    fingerprint the hiring manager opening someone's portfolio before
                    asking them anything. V2 gates all attribution on consent.
                  - V1 mounts the Maya chat widget. A marketing chat bot does not
                    belong on top of a person's credential.

                Same no-leading-slash rule as the route above, for the same reason.
            */}
            <Route path="p/:slug" element={<CapstoneRecordPage />} />
          </Route>
          {/*
              portfolio/:slug is the PERSON; p/:slug is one project. Spelled out rather
              than /u/, because /u/ and /p/ are one character apart carrying the SAME
              slug, and Ali reached for the wrong one within a minute of it shipping. A
              student sending a recruiter the wrong page would not find out until the
              recruiter had already read it.

              Ranked above /portfolio/share/:token by React Router's specificity rules,
              since a static segment beats a dynamic one. `share` is also a reserved
              slug, so nobody can mint an address that route would shadow.

              IT LEFT PublicLayoutV2 ON 2026-09-03, on Ali's decision, and the reasoning
              that put it there is worth answering rather than deleting:

                - "V2 is the site's real header." True, but this page now carries the
                  Refactored.ai wordmark and its own section nav. Nesting it produced
                  TWO stacked sticky bars and two brands arguing on one page.
                - "V1 fingerprints the hiring manager; V2 gates on consent." Standing
                  outside BOTH layouts is strictly better on that reasoning, not worse:
                  no tracker initialises here at all, consented or otherwise.
                - "V1 mounts the Maya chat widget." Still true, and still avoided.

              So this is the same argument reaching a further conclusion, not a reversal
              of it. These pages are also headed for their own URL, and a page that
              already renders standalone is the one that survives that move unchanged.
          */}
          <Route path="/portfolio/:slug" element={<PublicPortfolioProfilePage />} />
          {/*
              Client reviewer sign-in. Deliberately OUTSIDE both marketing layouts,
              for the same reasons that moved p/:slug out of the legacy block — and
              they are sharper here, because this page is the front door to a paying
              client's engagement:

                - PublicLayout (V1) calls initTracker() UNCONDITIONALLY. That would
                  fingerprint a client's executive reviewer before they have signed
                  in or consented to anything. They are not a lead.
                - V1 is the retired navbar and marketing footer, offering "Start free"
                  and "Book a walkthrough" to someone who is already a customer.
                - V1 mounts the Maya sales chat widget. A prospecting bot does not
                  belong on the door to a delivery review.

              V2 was not the answer either: it is still the marketing site. This surface
              wants no site chrome at all, so it gets none. The page supplies its own
              full-height centred layout.
          */}
          <Route path="/client" element={<ClientSignIn />} />
          {/*
              Where sign-in actually goes. Same standalone treatment and the same reason:
              a client reviewer is not a lead, so no marketing chrome and no tracker.
          */}
          <Route path="/client/projects" element={<ClientPortal />} />
          <Route element={<PublicLayout />}>
            {publicRoutes}
          </Route>
        </Routes>
      </Suspense>
      {/* GlobalCoryWidget removed — replaced by ArchitectChat on portal pages */}
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
